import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * Standard icon-only action button for tables and toolbars (edit / delete / restore
 * row actions). Renders a 36×36 tap target by default — comfortably above the WCAG
 * 2.2 AA 24px minimum (SC 2.5.8) — with consistent hover and disabled states.
 *
 * Put a single 16px lucide icon inside: `<IconButton variant="destructive"><Trash2 className="h-4 w-4" /></IconButton>`.
 * Use `size="sm"` (32px) only for genuinely dense rows where 36px would bloat the layout.
 */
export type IconButtonVariant = 'default' | 'destructive' | 'primary';
export type IconButtonSize = 'sm' | 'md';

const VARIANT_CLASSES: Record<IconButtonVariant, string> = {
  // Neutral actions (edit, reset, view): grey, darkens on hover.
  default: 'text-gray-400 hover:bg-gray-100 hover:text-gray-700',
  // Destructive actions (delete): grey, turns red on hover (app-wide delete pattern).
  destructive: 'text-gray-400 hover:bg-red-50 hover:text-red-600',
  // Brand actions (restore, focus): cobalt tint on hover.
  primary: 'text-gray-500 hover:bg-[#EDE9FE] hover:text-[#290BD9]',
};

const SIZE_CLASSES: Record<IconButtonSize, string> = {
  sm: 'h-8 w-8', // 32px — dense rows only
  md: 'h-9 w-9', // 36px — default table/toolbar action
};

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { variant = 'default', size = 'md', className, type, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn(
        'flex flex-shrink-0 items-center justify-center rounded-md transition-colors disabled:pointer-events-none disabled:opacity-50',
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        className,
      )}
      {...props}
    />
  );
});

export default IconButton;
